<div class="modal-header">
	<h5 class="modal-title">
		<i class="bi bi-bar-chart-line me-2"></i>Kontroll-Auswertung
		<span *ngIf="projectName" class="text-muted fw-normal ms-1">— {{ projectName }}</span>
	</h5>
	<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
</div>

<div class="modal-body">
	<div *ngIf="loading" class="text-center text-muted py-4">
		<div class="spinner-border spinner-border-sm me-2"></div> Auswertung wird geladen…
	</div>

	<div *ngIf="error" class="alert alert-danger">
		Die Auswertung konnte nicht geladen werden.
	</div>

	<ng-container *ngIf="!loading && !error && ev as e">
		<div *ngIf="e.totalPoints === 0" class="text-muted text-center py-4">
			Dieses Projekt enthält noch keine Punkte.
		</div>

		<ng-container *ngIf="e.totalPoints > 0">
			<div *ngIf="interactive" class="ce-hint">
				<i class="bi bi-hand-index me-1"></i>Tipp: Kennzahl oder Zeile anklicken → Punktliste auf diese Punkte filtern.
			</div>

			<!-- ===== Punktstatus ===== -->
			<section class="ce-section">
				<div class="ce-section-title">
					<span><i class="bi bi-diagram-3 me-1"></i>Punktstatus</span>
					<span class="ce-context">{{ e.totalPoints }} Punkte</span>
				</div>

				<div class="stacked-bar" role="img" aria-label="Verteilung der Punktstatus">
					<div *ngFor="let s of visibleSlices"
						class="stacked-seg" [ngClass]="badge(s.status)?.bgClass"
						[style.width.%]="pct(s.count)"
						[title]="badge(s.status)?.label + ': ' + s.count"></div>
				</div>

				<div class="chip-row">
					<span class="chip" *ngFor="let s of visibleSlices" [class.clickable]="interactive"
						[title]="interactive ? ('Liste auf „' + badge(s.status)?.label + '“ filtern') : badge(s.status)?.label"
						(click)="drill({ kind: 'status', status: s.status }, badge(s.status)?.label || 'Status')">
						<span class="dot" [ngClass]="badge(s.status)?.bgClass"></span>
						<strong>{{ s.count }}</strong> {{ badge(s.status)?.label }}
					</span>
				</div>

				<div class="ce-summary">
					<i class="bi bi-check-circle text-success me-1"></i>
					<strong>{{ e.completedCount }}</strong> von {{ e.totalPoints }} abgeschlossen ({{ e.completedPct }} %)
				</div>
			</section>

			<!-- ===== Automatische Validierungen ===== -->
			<section class="ce-section">
				<div class="ce-section-title">
					<span><i class="bi bi-cpu me-1"></i>Automatische Validierungen</span>
					<span class="ce-context">vom System · je Punkt</span>
				</div>
				<div class="chip-row">
					<span class="chip" [class.clickable]="interactive && e.autoValidation.danger > 0"
						[title]="interactive ? 'Liste auf Punkte mit Fehler filtern' : ''"
						(click)="drill({ kind: 'auto', severity: 'danger' }, 'Autom. Fehler')">
						<span class="dot danger"></span><strong>{{ e.autoValidation.danger }}</strong> Fehler</span>
					<span class="chip" [class.clickable]="interactive && e.autoValidation.warning > 0"
						[title]="interactive ? 'Liste auf Punkte mit Warnung filtern' : ''"
						(click)="drill({ kind: 'auto', severity: 'warning' }, 'Autom. Warnung')">
						<span class="dot warning"></span><strong>{{ e.autoValidation.warning }}</strong> Warnung</span>
					<span class="chip"><span class="dot ok"></span><strong>{{ e.totalPoints - e.autoValidation.pointsWithIssues }}</strong> ok</span>
				</div>
			</section>

			<!-- ===== Kommentare aus der Kontrolle ===== -->
			<section class="ce-section">
				<div class="ce-section-title">
					<span><i class="bi bi-chat-left-text me-1"></i>Kommentare aus der Kontrolle</span>
					<span class="ce-context">von Prüfern · je Feld · {{ e.totalComments }} gesamt</span>
				</div>
				<div class="chip-row">
					<span class="chip" [class.clickable]="interactive && e.danger.open > 0"
						[title]="interactive ? 'Liste auf Punkte mit offenem roten Kommentar filtern' : ''"
						(click)="drill({ kind: 'comment', severity: 'danger', resolved: false }, 'Rot offen')">
						<span class="dot danger"></span><strong>{{ e.danger.open }}</strong> Rot offen · {{ e.danger.resolved }} erledigt</span>
					<span class="chip" [class.clickable]="interactive && e.warning.open > 0"
						[title]="interactive ? 'Liste auf Punkte mit offenem gelben Kommentar filtern' : ''"
						(click)="drill({ kind: 'comment', severity: 'warning', resolved: false }, 'Gelb offen')">
						<span class="dot warning"></span><strong>{{ e.warning.open }}</strong> Gelb offen · {{ e.warning.resolved }} erledigt</span>
					<span class="chip" [class.clickable]="interactive && e.ok.open > 0"
						[title]="interactive ? 'Liste auf Punkte mit offenem Info-Kommentar filtern' : ''"
						(click)="drill({ kind: 'comment', severity: 'ok', resolved: false }, 'Weiß offen')">
						<span class="dot info-white"></span><strong>{{ e.ok.open }}</strong> Weiß offen · {{ e.ok.resolved }} erledigt</span>
				</div>
				<div class="ce-summary">
					{{ e.pointsWithComments }} Punkte beanstandet · {{ e.pointsClean }} unbeanstandet ·
					<strong>{{ e.discussionsOpen }}</strong> offene Diskussionen (von {{ e.discussionsTotal }})
				</div>

				<div *ngIf="e.topFields.length > 0" class="ce-list mt-2">
					<div class="ce-list-head">Häufigste beanstandete Felder</div>
					<ul class="stat-list">
						<li *ngFor="let f of e.topFields" [class.clickable]="interactive"
							[title]="interactive ? ('Liste auf Punkte mit Kommentar am Feld „' + displayString(f.field) + '“ filtern') : ''"
							(click)="drill({ kind: 'field', field: f.field }, 'Feld: ' + displayString(f.field))">
							<span class="lbl">{{ displayString(f.field) }}</span>
							<span class="badge bg-secondary">{{ f.count }}</span>
						</li>
					</ul>
				</div>
			</section>

			<!-- ===== Bearbeiter & Kontrolleure ===== (Punkte je Person — nicht fuer Datennutzer) -->
			<section class="ce-section" *ngIf="!personenVerborgen">
				<div class="ce-section-title">
					<span><i class="bi bi-people me-1"></i>Bearbeiter &amp; Kontrolleure</span>
					<span class="ce-context">Punkte je Person</span>
				</div>
				<div class="ce-list-grid">
					<div class="ce-list">
						<div class="ce-list-head"><span><i class="bi bi-pencil me-1"></i>Kartierer</span><span class="head-hint">bearb. · vorg.</span></div>
						<ul class="stat-list">
							<li *ngFor="let p of e.kartierer">
								<span class="lbl">{{ p.label }}</span>
								<span class="vals">
									<span class="num" [class.clickable]="interactive && p.bearbeitet > 0"
										[title]="interactive ? ('Liste auf von ' + p.label + ' bearbeitete Punkte filtern') : 'bearbeitet'"
										(click)="drill({ kind: 'person', activity: 'bearbeitet', name: p.name }, p.label + ' · bearbeitet')">{{ p.bearbeitet }}</span>
									<span class="num-sep">·</span>
									<span class="num" [class.clickable]="interactive && p.vorgelegt > 0"
										[title]="interactive ? ('Liste auf von ' + p.label + ' vorgelegte Punkte filtern') : 'vorgelegt'"
										(click)="drill({ kind: 'person', activity: 'vorgelegt', name: p.name }, p.label + ' · vorgelegt')">{{ p.vorgelegt }}</span>
								</span>
							</li>
							<li *ngIf="e.kartierer.length === 0" class="empty">—</li>
						</ul>
					</div>
					<div class="ce-list">
						<div class="ce-list-head"><span><i class="bi bi-check2-square me-1"></i>Kontrolleure</span><span class="head-hint">abgeschl.</span></div>
						<ul class="stat-list">
							<li *ngFor="let p of e.kontrolleure" [class.clickable]="interactive"
								[title]="interactive ? ('Liste auf von ' + p.label + ' abgeschlossene Punkte filtern') : ''"
								(click)="drill({ kind: 'person', activity: 'abgeschlossen', name: p.name }, p.label + ' · abgeschlossen')">
								<span class="lbl">{{ p.label }}</span>
								<span class="badge bg-secondary">{{ p.count }}</span>
							</li>
							<li *ngIf="e.kontrolleure.length === 0" class="empty">—</li>
						</ul>
					</div>
				</div>
			</section>
		</ng-container>
	</ng-container>
</div>

<div class="modal-footer">
	<button type="button" class="btn btn-secondary" (click)="activeModal.dismiss()">Schließen</button>
</div>
